import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { CREATE_VOTING_ARTICLE } from '../graphql/voting';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useTags } from '../contexts/TagContext';
import VotingDetailModal from '../components/VotingDetailModal';
import { useAuth } from '../contexts/AuthContext';

const CreateVoting = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    allowComments: true,
    showVoteResults: true,
    isAnonymous: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    title: '',
    description: '',
    type: 'SINGLE_CHOICE',
    options: [
      { text: '', order: 0 },
      { text: '', order: 1 }
    ],
    tags: [],
    visibility: 'PUBLIC',
    secret: false
  });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [createdVotingId, setCreatedVotingId] = useState(null);
  
  // 獲取標籤列表
  const { tags: availableTags } = useTags();

  const [createVotingArticle] = useMutation(CREATE_VOTING_ARTICLE, {
    onCompleted: (data) => {
      if (data.createVotingArticle.success) {
        setCreatedVotingId(data.createVotingArticle.article.id);
        setShowDetailModal(true);
      } else {
        alert('創建失敗：' + data.createVotingArticle.message);
      }
      setIsSubmitting(false);
    },
    onError: (error) => {
      console.error('創建投票失敗:', error);
      alert('創建失敗：' + error.message);
      setIsSubmitting(false);
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 驗證問題
    if (!newQuestion.title.trim()) {
      alert('請輸入問題標題');
      return;
    }
    
    if (newQuestion.options.filter(opt => opt.text.trim()).length < 2) {
      alert('至少需要兩個選項');
      return;
    }
    
    if (newQuestion.tags.length < 1) {
      alert('至少需要一個標籤');
      return;
    }
    
    if (newQuestion.tags.length > 2) {
      alert('最多隻能選擇兩個標籤');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 先建立問題
      const createQuestionQuery = `
        mutation CreateQuestion($input: CreateQuestionInput!) {
          createQuestion(input: $input) { 
            success 
            question { id } 
            errors { field message } 
          }
        }
      `;
      
      const validOptions = newQuestion.options
        .filter(opt => opt.text.trim())
        .map((opt, index) => ({ text: opt.text.trim(), order: index }));
      
      const questionInput = {
        ...newQuestion,
        options: validOptions,
        tags: newQuestion.tags.filter(tag => tag.trim()),
        secret: newQuestion.secret
      };
      
      const { data: questionData } = await client.mutate({
        mutation: gql(createQuestionQuery),
        variables: { input: questionInput }
      });
      
      if (!questionData.createQuestion.success) {
        alert('建立問題失敗：' + questionData.createQuestion.errors.map(e => e.message).join(', '));
        setIsSubmitting(false);
        return;
      }
      
      // 使用新建立的問題創建投票
      await createVotingArticle({
        variables: {
          input: {
            questionId: questionData.createQuestion.question.id,
            status: 'PUBLISHED',
            allowComments: formData.allowComments,
            showVoteResults: formData.showVoteResults,
            isAnonymous: formData.isAnonymous
          }
        }
      });
    } catch (error) {
      console.error('提交失敗:', error);
      alert('創建失敗：' + error.message);
      setIsSubmitting(false);
    }
  };

  const handleNewQuestionChange = (field, value) => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, value) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, text: value } : opt
      )
    }));
  };

  const addOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      options: [...prev.options, { text: '', order: prev.options.length }]
    }));
  };

  const removeOption = (index) => {
    if (newQuestion.options.length > 2) {
      setNewQuestion(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAddTag = (tagName) => {
    if (tagName && !newQuestion.tags.includes(tagName) && newQuestion.tags.length < 2) {
      setNewQuestion(prev => ({
        ...prev,
        tags: [...prev.tags, tagName]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setNewQuestion(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">創建新投票</h1>
            <button
              onClick={() => navigate('/voting')}
              className="px-4 py-2 bg-purple-400 text-white rounded-lg hover:bg-purple-500 transition-colors"
            >
              返回廣場
            </button>
          </div>
          <p className="text-gray-600">創建一個有趣的投票讓大家參與討論</p>
        </div>

        {/* 表單 */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 問題設定 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                問題設定 *
              </label>
              
              {/* 新問題建立表單 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    問題標題 * <span className="text-gray-500 text-xs">({newQuestion.title.length}/30)</span>
                  </label>
                  <input
                    type="text"
                    value={newQuestion.title}
                    onChange={(e) => {
                      if (e.target.value.length <= 30) {
                        handleNewQuestionChange('title', e.target.value);
                      }
                    }}
                    placeholder="輸入問題標題..."
                    maxLength={30}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    問題描述
                  </label>
                  <textarea
                    value={newQuestion.description}
                    onChange={(e) => {
                      handleNewQuestionChange('description', e.target.value);
                      // 自動調整高度
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="描述這個問題..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none overflow-hidden"
                    style={{ minHeight: '60px' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    選項設定
                  </label>
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <span className="text-sm text-gray-500 w-8">{index + 1}.</span>
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder="輸入選項..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      {newQuestion.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="mt-2 px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                  >
                    + 新增選項
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    標籤 * <span className="text-gray-500 text-xs">(至少1個，最多2個)</span>
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddTag(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={!availableTags || availableTags.length === 0 || newQuestion.tags.length >= 2}
                  >
                    <option value="">選擇標籤...</option>
                    {availableTags
                      ?.filter(tag => !newQuestion.tags.includes(tag.name))
                      .map(tag => (
                        <option key={tag.id} value={tag.name}>
                          {tag.name}
                          {tag.description && ` - ${tag.description}`}
                        </option>
                      ))}
                  </select>
                  {newQuestion.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newQuestion.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded flex items-center"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 text-purple-600 hover:text-purple-800"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div 
                  className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
                  onClick={() => handleNewQuestionChange('secret', !newQuestion.secret)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                      🔒 祕密問題
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNewQuestionChange('secret', true);
                        }}
                        className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                          newQuestion.secret
                            ? 'bg-purple-500 text-white border-purple-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        是
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNewQuestionChange('secret', false);
                        }}
                        className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                          !newQuestion.secret
                            ? 'bg-purple-500 text-white border-purple-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                        }`}
                      >
                        否
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">
                    啟用後，此問題將不會顯示在「配對詳情」和「共同話題」中，但仍會參與配對分數計算
                  </p>
                </div>
              </div>
            </div>

            {/* 選項 */}
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
              >
                <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                  允許評論
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({ ...prev, allowComments: true }));
                    }}
                    className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                      formData.allowComments
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    是
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({ ...prev, allowComments: false }));
                    }}
                    className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                      !formData.allowComments
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    否
                  </button>
                </div>
              </div>
              
              <div 
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setFormData(prev => ({ ...prev, showVoteResults: !prev.showVoteResults }))}
              >
                <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                  顯示投票結果
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({ ...prev, showVoteResults: true }));
                    }}
                    className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                      formData.showVoteResults
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    是
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData(prev => ({ ...prev, showVoteResults: false }));
                    }}
                    className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                      !formData.showVoteResults
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    否
                  </button>
                </div>
              </div>
              
              <div 
                className="p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => setFormData(prev => ({ ...prev, isAnonymous: !prev.isAnonymous }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                    🎭 匿名發布
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, isAnonymous: true }));
                      }}
                      className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                        formData.isAnonymous
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      是
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, isAnonymous: false }));
                      }}
                      className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                        !formData.isAnonymous
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      否
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  啟用後，你的身份將對所有使用者（包括管理員）完全隱藏，但你仍可在「我的投票」中查看此投票文
                </p>
              </div>
            </div>

            {/* 按鈕 */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/voting')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-lg hover:from-pink-500 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? '創建中...' : '創建投票'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 投票詳情彈窗 */}
      <VotingDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setCreatedVotingId(null);
          // 創建成功後導航回投票廣場
          navigate('/voting');
        }}
        votingId={createdVotingId}
        viewMode="myPosts"
        currentUserId={user?.id}
      />
    </div>
  );
};

export default CreateVoting; 